import { Box, Flex } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CenteredLoadingIndicator } from '../../../components/LoadingIndicator';
import ServerValidationBox from '../../../components/ServerValidationBox';
import { PHRHealthCondition } from '../../../generated';
import { clearPHRPatientErrorAction } from '../../../store/patient/phr/phrActions';
import { IPHRPatientCombined } from '../../../store/patient/phr/phrReducers';
import { phrHealthConditionsSelector } from '../../../store/patient/phr/phrSelectors';
import { deleteForPhrSection, getAllForPhrSection } from '../../../store/root-creator';
import { AppState } from '../../../store/root-reducers';
import { ReduxPHRCategoryEnum } from '../../../store/types';
import { scrollToTop } from '../../../utils/scrollToTop';
import { AddPHRItemButton } from '../components/PhrItem';
import PhrItemDeleteModal from '../components/PhrItemDeleteModal';
import { PhrFormButtons } from '../components/PhrItemForm';
import { PhrDetailsButtons } from '../components/PhrItemView';
import HealthConditionForm from './HealthConditionForm';
import HealthConditionList from './HealthConditionList';
import HealthConditionView from './HealthConditionView';

interface IProps {
  patient: IPHRPatientCombined;
  renderHeader: (onCreate?: any, onBack?: () => void, title?: string, showDismiss?: boolean) => any;
}

enum EHealthConditionsViewState {
  List = 'List',
  View = ' View',
  Edit = 'Edit',
}

const HealthConditionsComponent = ({ patient: phrPatient, renderHeader }: IProps) => {
  const dispatch = useDispatch();
  const innerBtnRef = useRef<HTMLButtonElement>(null);
  const phrHealthConditions = phrHealthConditionsSelector(phrPatient.phr.phrHealthConditions);
  const { isPhrLoading, error } = useSelector((state: AppState) => state.phrPatientState);
  const [currentHealthCondition, setCurrentHealthCondition] = useState<PHRHealthCondition | null>(
    null,
  );
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<EHealthConditionsViewState>(
    EHealthConditionsViewState.List,
  );

  useEffect(() => {
    if (phrPatient) {
      dispatch(
        getAllForPhrSection(ReduxPHRCategoryEnum.PHRHealthConditions, phrPatient.phr.patientID),
      );
    }
    // avoid re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const onCreate = () => {
    setCurrentHealthCondition(null);
    setCurrentView(EHealthConditionsViewState.Edit);
  };

  const onEdit = (phrHealthCondition: PHRHealthCondition) => {
    setCurrentHealthCondition(phrHealthCondition);
    setCurrentView(EHealthConditionsViewState.Edit);
  };

  const onView = (phrHealthCondition: PHRHealthCondition) => {
    setCurrentHealthCondition(phrHealthCondition);
    setCurrentView(EHealthConditionsViewState.View);
  };

  const onDelete = (phrHealthConditionID: string) => {
    setShowDeleteModal(true);
    setRecordToDelete(phrHealthConditionID);
  };

  const confirmDelete = async () => {
    setCurrentView(EHealthConditionsViewState.List);
    setShowDeleteModal(false);
    await dispatch(
      deleteForPhrSection(
        ReduxPHRCategoryEnum.PHRHealthConditions,
        recordToDelete,
        phrPatient.phr.patientID,
        false,
      ),
    );
  };

  useEffect(() => {
    dispatch(clearPHRPatientErrorAction());
    scrollToTop();
  }, [dispatch]);

  return (
    <Box paddingY="2rem" backgroundColor="#f4f4f4" height="100%">
      {currentView === EHealthConditionsViewState.List ? (
        renderHeader()
      ) : (
        <Flex>
          {renderHeader(null, () => setCurrentView(EHealthConditionsViewState.List), '', false)}
          {currentView === EHealthConditionsViewState.View && (
            <PhrDetailsButtons
              isVerified={currentHealthCondition?.verified}
              phrItem={currentHealthCondition}
              phrItemID={currentHealthCondition?.phrHealthConditionID}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          )}
          {currentView === EHealthConditionsViewState.Edit && (
            <PhrFormButtons
              innerBtnRef={innerBtnRef}
              toggleView={() => setCurrentView(EHealthConditionsViewState.List)}
            />
          )}
        </Flex>
      )}

      {currentView === EHealthConditionsViewState.List && (
        <>
          <AddPHRItemButton name="Health Condition" onCreate={onCreate} />
          <ServerValidationBox message={error} />
          {isPhrLoading ? (
            <Box marginTop="1rem">
              <CenteredLoadingIndicator />
            </Box>
          ) : (
            <HealthConditionList phrHealthConditions={phrHealthConditions} onView={onView} />
          )}
        </>
      )}

      {currentView === EHealthConditionsViewState.View && (
        <HealthConditionView healthCondition={currentHealthCondition!} />
      )}

      {currentView === EHealthConditionsViewState.Edit && (
        <HealthConditionForm
          currentHealthCondition={currentHealthCondition}
          patientID={phrPatient.phr.patientID}
          toggleView={() => setCurrentView(EHealthConditionsViewState.List)}
          innerBtnRef={innerBtnRef}
        />
      )}

      <PhrItemDeleteModal
        isOpen={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
      />
    </Box>
  );
};

export default HealthConditionsComponent;
